import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import ProductCombobox from "@/components/ProductCombobox";
import { toast } from "sonner";
import { Scroll, Wrench, Package, ArrowsClockwise, ClipboardText } from "@phosphor-icons/react";

const empty = { order_type: "film", product_id: "", item_name: "", note: "", quantity: "", unit: "rolls", length_per_roll_m: "", purchase_type: "resupply", order_reference: "" };

export default function AddOrderItemDialog({ open, onOpenChange, editItem, onSuccess }) {
  const [form, setForm] = useState(empty);
  const [products, setProducts] = useState([]);
  const [mfrs, setMfrs] = useState([]);
  const [series, setSeries] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    Promise.all([api.get("/products"), api.get("/manufacturers"), api.get("/series")])
      .then(([p, m, s]) => { setProducts(p.data); setMfrs(m.data); setSeries(s.data); })
      .catch(() => {});
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (editItem) {
      setForm({
        order_type: editItem.order_type,
        product_id: editItem.product_id || "",
        item_name: editItem.item_name || "",
        note: editItem.note || "",
        quantity: String(editItem.quantity ?? ""),
        unit: editItem.unit || "rolls",
        length_per_roll_m: editItem.length_per_roll_m != null ? String(editItem.length_per_roll_m) : "",
        purchase_type: editItem.purchase_type || "resupply",
        order_reference: editItem.order_reference || "",
      });
    } else {
      setForm(empty);
    }
  }, [editItem, open]);

  const mfrMap = useMemo(() => Object.fromEntries(mfrs.map((m) => [m.id, m])), [mfrs]);
  const seriesMap = useMemo(() => Object.fromEntries(series.map((s) => [s.id, s])), [series]);
  const filmProducts = useMemo(() => products.filter((p) => p.inventory_type !== "count"), [products]);
  const toolProducts = useMemo(() => products.filter((p) => p.inventory_type === "count"), [products]);
  const selectedFilm = useMemo(() => filmProducts.find((p) => p.id === form.product_id) || null, [filmProducts, form.product_id]);

  const setType = (t) => { if (t) setForm({ ...empty, order_type: t, unit: t === "film" ? "rolls" : "units" }); };

  const valid = () => {
    if (!form.quantity || Number(form.quantity) <= 0) return false;
    if (form.order_type === "other") return !!form.item_name.trim();
    if (form.purchase_type === "order" && !form.order_reference.trim()) return false;
    if (form.order_type === "film" && form.unit === "rolls" && (!form.length_per_roll_m || Number(form.length_per_roll_m) <= 0)) return false;
    return !!form.product_id;
  };

  const onSave = async () => {
    setSaving(true);
    try {
      const payload = { order_type: form.order_type, quantity: Number(form.quantity), note: form.note };
      if (form.order_type === "other") {
        payload.item_name = form.item_name;
        payload.unit = "units";
      } else {
        payload.product_id = form.product_id;
        payload.unit = form.order_type === "film" ? form.unit : "units";
        if (form.order_type === "film" && form.unit === "rolls") {
          payload.length_per_roll_m = form.length_per_roll_m ? Number(form.length_per_roll_m) : null;
        }
        payload.purchase_type = form.purchase_type;
        payload.order_reference = form.purchase_type === "order" ? form.order_reference.trim() : "";
      }
      if (editItem) await api.put(`/order-items/${editItem.id}`, payload);
      else await api.post("/order-items", payload);
      toast.success(editItem ? "Order item updated" : "Added to order list");
      onOpenChange(false);
      onSuccess?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  const TYPES = [
    { v: "film", label: "Film", Icon: Scroll },
    { v: "tools", label: "Tools", Icon: Wrench },
    { v: "other", label: "Other", Icon: Package },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="add-order-item-dialog">
        <DialogHeader>
          <DialogTitle>{editItem ? "Edit order item" : "Add item to order list"}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">Type</Label>
            <ToggleGroup type="single" value={form.order_type} onValueChange={setType} className="justify-start gap-2" disabled={!!editItem}>
              {TYPES.map(({ v, label, Icon }) => (
                <ToggleGroupItem key={v} value={v} data-testid={`order-type-${v}`}
                  className="gap-2 border border-slate-200 data-[state=on]:bg-slate-900 data-[state=on]:text-white rounded-md px-4">
                  <Icon size={16} /> {label}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
          </div>

          {form.order_type !== "other" && (
            <div className="space-y-2">
              <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">Purpose</Label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { key: "resupply", Icon: ArrowsClockwise, title: "Resupply", desc: "Restock — becomes active inventory when received." },
                  { key: "order", Icon: ClipboardText, title: "Purchase for order", desc: "Bought for a job — consumed on arrival (not stocked)." },
                ].map(({ key, Icon, title, desc }) => {
                  const active = form.purchase_type === key;
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setForm({ ...form, purchase_type: key, order_reference: key === "order" ? form.order_reference : "" })}
                      data-testid={`purchase-type-${key}`}
                      className={`text-left flex items-start gap-2 rounded-md border p-2.5 transition-colors ${active ? "border-blue-500 bg-blue-50/60 ring-1 ring-blue-500" : "border-slate-200 hover:bg-slate-50"}`}
                    >
                      <Icon size={18} weight={active ? "fill" : "regular"} className={active ? "text-blue-600 mt-0.5 shrink-0" : "text-slate-500 mt-0.5 shrink-0"} />
                      <div>
                        <div className="text-sm font-semibold text-slate-900">{title}</div>
                        <div className="text-[11px] text-slate-500 mt-0.5 leading-snug">{desc}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
              {form.purchase_type === "order" && (
                <div className="space-y-1.5">
                  <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">Job / order number</Label>
                  <Input
                    value={form.order_reference}
                    onChange={(e) => setForm({ ...form, order_reference: e.target.value })}
                    placeholder="e.g. Job #1234 / customer name"
                    data-testid="order-reference"
                  />
                  <p className="text-[11px] text-slate-400">Required. Kept with the stock when it arrives (marked consumed for this job).</p>
                </div>
              )}
            </div>
          )}

          {form.order_type === "film" && (
            <>
              <div className="space-y-1.5">
                <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">Film (Brand · Series · Product)</Label>
                <ProductCombobox products={filmProducts} mfrMap={mfrMap} seriesMap={seriesMap}
                  value={form.product_id} onChange={(v) => {
                    const prod = filmProducts.find((p) => p.id === v);
                    setForm((f) => ({
                      ...f,
                      product_id: v,
                      length_per_roll_m: prod?.full_roll_length_m != null ? String(prod.full_roll_length_m) : f.length_per_roll_m,
                    }));
                  }} />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">Qty</Label>
                  <Input type="number" min="0" step="1" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} data-testid="order-qty" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">Unit</Label>
                  <Select value={form.unit} onValueChange={(v) => setForm({ ...form, unit: v })}>
                    <SelectTrigger data-testid="order-unit"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="rolls">Rolls</SelectItem>
                      <SelectItem value="meters">Meters</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {form.unit === "rolls" && (
                  <div className="space-y-1.5">
                    <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">m / roll</Label>
                    <Input type="number" min="0" step="0.01" value={form.length_per_roll_m} onChange={(e) => setForm({ ...form, length_per_roll_m: e.target.value })} data-testid="order-lpr" />
                  </div>
                )}
              </div>
              {form.unit === "rolls" && selectedFilm?.full_roll_length_m != null && (
                <p className="text-[11px] text-slate-500 -mt-1" data-testid="order-lpr-hint">
                  Auto-filled from the product's full roll length ({selectedFilm.full_roll_length_m}m). Change only if this order differs.
                </p>
              )}
              {form.unit === "rolls" && form.product_id && selectedFilm?.full_roll_length_m == null && (
                <p className="text-[11px] text-amber-600 -mt-1" data-testid="order-lpr-nohint">
                  This product has no full roll length set — add one on the product to auto-fill it next time.
                </p>
              )}
            </>
          )}

          {form.order_type === "tools" && (
            <>
              <div className="space-y-1.5">
                <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">Tool (Series · Product)</Label>
                <ProductCombobox products={toolProducts} mfrMap={mfrMap} seriesMap={seriesMap}
                  value={form.product_id} onChange={(v) => setForm({ ...form, product_id: v })} />
              </div>
              <div className="space-y-1.5 max-w-[140px]">
                <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">Qty (units)</Label>
                <Input type="number" min="0" step="1" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} data-testid="order-qty" />
              </div>
            </>
          )}

          {form.order_type === "other" && (
            <>
              <div className="space-y-1.5">
                <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">Item name</Label>
                <Input value={form.item_name} onChange={(e) => setForm({ ...form, item_name: e.target.value })} placeholder="e.g. Cutting blades" data-testid="order-item-name" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">Qty</Label>
                  <Input type="number" min="0" step="1" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} data-testid="order-qty" />
                </div>
              </div>
            </>
          )}

          <div className="space-y-1.5">
            <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">Note (optional)</Label>
            <Textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Any details for whoever places the order…" data-testid="order-note" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-md">Cancel</Button>
          <Button onClick={onSave} disabled={!valid() || saving} className="bg-blue-600 hover:bg-blue-700 rounded-md" data-testid="order-save-btn">
            {saving ? "Saving…" : editItem ? "Save" : "Add to list"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
